import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ImagePlus } from "lucide-react";
import { IMAGES } from "../../lib/constants";

export const Gallery = () => {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="galeria" className="relative py-20 md:py-28 bg-bege" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <div className="text-xs tracking-[0.35em] uppercase text-gold font-semibold mb-3">galeria</div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-bordo font-semibold" data-testid="gallery-title">
            Nossa Casa, <span className="italic">Nossa História</span>
          </h2>
          <div className="gold-ornament mt-5" />
          <p className="mt-6 text-dark/70 text-base">
            Cada canto guarda uma memória. Cada prato, uma lembrança.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
          {IMAGES.gallery.map((src, i) => (
            <motion.button
              key={i}
              type="button"
              onClick={() => setLightbox(src)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className={`group relative overflow-hidden rounded-2xl shadow-md border border-gold/20 card-hover bg-white ${
                i === 0 ? "md:col-span-2 md:row-span-2 aspect-square md:aspect-auto" : "aspect-square"
              }`}
              data-testid={`gallery-item-${i}`}
              aria-label={`Foto ${i + 1} da galeria`}
            >
              <img
                src={src}
                alt={`Restaurante O Padrinho — foto ${i + 1}`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.button>
          ))}

          {/* Placeholder for future photos */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="aspect-square rounded-2xl border-2 border-dashed border-gold/40 flex flex-col items-center justify-center text-center p-6 bg-white/40"
            data-testid="gallery-placeholder"
          >
            <ImagePlus size={36} className="text-gold mb-3" strokeWidth={1.4} />
            <p className="text-xs md:text-sm text-bordo/80 font-medium">
              Em breve, mais sabores aqui.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-5 right-5 text-white p-2 hover:text-gold transition-colors"
              aria-label="Fechar"
              data-testid="gallery-lightbox-close"
            >
              <X size={28} />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              src={lightbox}
              alt="Foto ampliada"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
